import { FilterArea } from "#src/components/FilterArea";
import { FilterDrawer } from "#src/components/FilterDrawer";
import { useCustomAttributeFiltersV2 } from "#src/components/hooks/FilterPanel/useCustomAttributeFilters";
import { DEFAULT_DATE_RANGES } from "#src/hooks/useDateRange";
import useLocalization from "#src/hooks/useLocalization";
import {
  Accordion,
  DateSelectorInput,
  DropdownInput,
  TextInput,
  type StorageKeys,
} from "@validereinc/common-components";
import { AssetType } from "@validereinc/domain";
import { AssetGroups } from "@validereinc/domain-controllers";
import React from "react";

const { AssetGroupStatusOptions } = AssetGroups.view;

export const NetworksViewFilterArea = ({
  viewConfigStorageKey,
}: Pick<StorageKeys, "viewConfigStorageKey">) => {
  const { localize } = useLocalization();

  return (
    <FilterArea.Root<{
      period: { from: Date; to: Date };
    }>
      storageKey={viewConfigStorageKey}
      defaultValues={{
        period: DEFAULT_DATE_RANGES.currentWholeMonth,
      }}
    >
      <FilterArea.Container
        aria-label={`View Filters for ${localize(`${AssetType.ASSET_GROUP}_plural`)}`}
      >
        <FilterArea.Content>
          {({ handleOnChange }) => (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 16,
              }}
            >
              <DateSelectorInput
                name={"period"}
                variant="month"
                label="Period"
                isInline
                isLabelShown={false}
                onChange={(val) => handleOnChange(val, "period")}
              />
            </div>
          )}
        </FilterArea.Content>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};

export const NetworksTableFilterAreaContent = ({
  hasCustomAttributeFilters,
}: {
  hasCustomAttributeFilters?: boolean;
}) => {
  const { localize } = useLocalization();
  const { customAttributeFilters: networkCustomAttrFilters } =
    useCustomAttributeFiltersV2({
      assetType: AssetType.ASSET_GROUP,
      enabled: hasCustomAttributeFilters,
    });

  return (
    <>
      <Accordion defaultActiveKeys={[AssetType.ASSET_GROUP]}>
        <Accordion.AccordionPanel
          dataKey={AssetType.ASSET_GROUP}
          title={localize(`${AssetType.ASSET_GROUP}_plural`)}
        >
          <DropdownInput
            name="status"
            label="Status"
            options={AssetGroupStatusOptions}
            placeholder="Select Status..."
            labelKey="label"
            valueKey="value"
            isMulti
            isFluid
            isSearchable
            isOptionalTextShown={false}
          />
          {hasCustomAttributeFilters ? networkCustomAttrFilters : null}
        </Accordion.AccordionPanel>
      </Accordion>
    </>
  );
};

export const NetworksTableFilterArea = ({
  filterConfigStorageKey,
  hasCustomAttributeFilters = true,
}: Pick<StorageKeys, "filterConfigStorageKey"> & {
  hasCustomAttributeFilters?: boolean;
}) => {
  const { localize } = useLocalization();

  return (
    <FilterArea.Root storageKey={filterConfigStorageKey}>
      <FilterArea.Container
        aria-label={`Filters for ${localize(`${AssetType.ASSET_GROUP}_plural`)}`}
      >
        <FilterDrawer.Root>
          <FilterArea.Content>
            {({ handleOnChange }) => (
              <div style={{ marginRight: 8, marginBottom: 0 }}>
                <TextInput
                  name="name"
                  label="Search"
                  isLabelShown={false}
                  placeholder={`Search ${localize(`${AssetType.ASSET_GROUP}_plural`)}...`}
                  type="search"
                  isInline
                  onChange={(val) => handleOnChange(val, "name")}
                />
              </div>
            )}
          </FilterArea.Content>
          <FilterDrawer.Trigger />
          <FilterDrawer.Content>
            <NetworksTableFilterAreaContent
              hasCustomAttributeFilters={hasCustomAttributeFilters}
            />
          </FilterDrawer.Content>
        </FilterDrawer.Root>
      </FilterArea.Container>
    </FilterArea.Root>
  );
};
